import { useEffect } from 'react'
import { Radio, RadioGroup, Skeleton } from '@heroui/react'
import { RefundAction } from '@itsrever/returns-api-types'
import { usePostHog } from '@posthog/react'
import { useTranslation } from 'react-i18next'
import {
  ReturnsItemToReturnActionEnum,
  type ReturnsExchangeTimingOption
} from '@itsrever/rever-api'
import { useExchangeTimings } from '@/hooks/use-exchange-timings'
import { useAppSelector } from '@/redux/hooks'
import { FlashIcon } from '@/ui/icons/flash'
import { useTranslate } from '@/hooks/useTranslate'
import { TimerIcon } from '@/ui/icons/timer'

// To accommodate type differences due to a mismatch between the REST and gRPC endpoints
const ItemToReturnAction = {
  [RefundAction.RefundAction_NO_ACTION]: ReturnsItemToReturnActionEnum.NoAction,
  [RefundAction.RefundAction_TO_EXCHANGE]:
    ReturnsItemToReturnActionEnum.ToExchange,
  [RefundAction.RefundAction_TO_RETURN]: ReturnsItemToReturnActionEnum.ToReturn
} as const

interface ShippingTimingProps {
  onSelect: (isInstant: boolean) => void
}

export const ShippingTiming = ({ onSelect }: ShippingTimingProps) => {
  const { translate } = useTranslate()

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const returnState = useAppSelector((store) => store.return)

  const { data, status } = useExchangeTimings({
    ecommerceId: ecommerceId ?? '',
    returnsExchangeTimingsRequest: {
      exchange_selections: returnState.newItems.map((item) => ({
        line_item_platform_id: item.returnedItemId,
        quantity: item.quantity,
        variant_id: item.productVariant.variantId,
        signed_product_raw: item.signedProductRaw,
        collection_ids: item.collectionIds
      })),
      items_to_return: returnState.returnedItems.map((item) => ({
        ...item.item,
        action: ItemToReturnAction[item.item.action]
      })),
      checkout_id: checkoutId,
      ecommerce_id: ecommerceId
    }
  })

  if (status === 'error') {
    return null
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="space-y-0.5">
          <h2
            className="text-base font-medium"
            dangerouslySetInnerHTML={{
              __html: translate('confirmation_page_shipping_timing_title')
            }}
          />
          <h3
            className="text-[0.8125rem] font-normal leading-normal text-neutral-500"
            dangerouslySetInnerHTML={{
              __html: translate('confirmation_page_shipping_timing_description')
            }}
          />
        </div>
      </div>
      {status === 'pending' ? (
        <Skeleton className="h-36 w-full rounded-md" />
      ) : null}
      {status === 'success' && data?.timing_options && (
        <ShippingTimingOptions
          options={data.timing_options}
          onSelect={onSelect}
        />
      )}
    </div>
  )
}

const ShippingTimingOptions = ({
  options,
  onSelect
}: {
  options: ReturnsExchangeTimingOption[]
  onSelect: (isInstant: boolean) => void
}) => {
  const { i18n } = useTranslation()
  const { translate } = useTranslate()

  const posthog = usePostHog()

  const orderMoneyFormat =
    useAppSelector(
      (store) => store.returnsApi.order.response?.order?.money_format
    ) ?? {}

  useEffect(() => {
    posthog.capture('Shipping Timing loaded', {
      options
    })
  }, [options, posthog])

  return (
    <RadioGroup
      aria-label="Select the shipping timing"
      defaultValue={options.find((option) => option.is_default)?.timing ?? ''}
      classNames={{
        wrapper:
          'gap-0 rounded-md border border-solid border-neutral-200 divide-y divide-neutral-200 overflow-hidden'
      }}
    >
      {options.map((option) => {
        return (
          <Radio
            key={option.timing}
            value={option.timing ?? ''}
            data-default={option.is_default ?? false}
            onChange={() => {
              if (!option.is_default)
                posthog.capture('InstantShipping Timing Selected', { option })

              onSelect(!option.is_default)
            }}
            size="sm"
            className="group"
            classNames={{
              base: 'items-start gap-1.5 group max-w-full m-0 px-4 py-4 data-[selected=true]:data-[default=true]:bg-neutral-50 data-[selected=true]:bg-blue-100/40',
              wrapper: 'group-data-[selected=true]:border-neutral-800',
              labelWrapper: 'w-full -mt-[0.1875rem] gap-0.5',
              control: 'size-2 group-data-[selected=true]:bg-neutral-800'
            }}
          >
            <div className="flex w-full items-start justify-between gap-4">
              <div className="flex flex-col gap-1">
                <div className="flex flex-col flex-wrap gap-1 md:flex-row md:items-center md:gap-2">
                  {option.is_default ? (
                    <p className="text-sm font-medium">
                      {translate('confirmation_page_shipping_timing_standard')}
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">
                        {translate('confirmation_page_shipping_timing_instant')}
                      </p>
                      <span className="inline-flex h-fit w-fit items-center gap-0.5 rounded bg-blue-100 py-1 pl-1.5 pr-2 text-xs font-medium uppercase leading-none text-blue-600">
                        <FlashIcon className="size-3 shrink-0" />
                        {translate('confirmation_page_shipping_timing_faster')}
                      </span>
                    </div>
                  )}
                  <strong className="flex items-center gap-1 text-xs font-medium text-neutral-500">
                    <TimerIcon strokeWidth={2} className="size-3 shrink-0" />
                    {translate(
                      `confirmation_page_shipping_timing_ships_in_${option.timing}`
                    )}
                  </strong>
                </div>
                <span className="text-sm font-normal text-neutral-500">
                  {translate(
                    `confirmation_page_shipping_timing_${option.timing}`
                  )}
                </span>
              </div>
              <span className="text-sm font-medium text-neutral-800">
                {option.price
                  ? (
                      Number(option.price ?? 0) /
                      (orderMoneyFormat.amount_multiplied_by ?? 100)
                    ).toLocaleString(i18n.language, {
                      style: 'currency',
                      currency: orderMoneyFormat.currency
                    })
                  : translate('confirmation_page_shipping_timing_free')}
              </span>
            </div>
          </Radio>
        )
      })}
    </RadioGroup>
  )
}
